"use client";

import { Link } from "react-router-dom";

import type { AlertDetailDto } from "@/lib/api/types";

export type AlertDetailViewProps = {
  alert: AlertDetailDto;
};

const SEVERITY_COLORS: Record<string, { bg: string; text: string; border: string; glow: string }> = {
  critical: { bg: "#3d1a1a", text: "#f87171", border: "#7f1d1d", glow: "#f8717144" },
  high:     { bg: "#3d2a10", text: "#fb923c", border: "#7c2d12", glow: "#fb923c44" },
  error:    { bg: "#3d1a1a", text: "#f87171", border: "#7f1d1d", glow: "#f8717144" },
  medium:   { bg: "#3a2e10", text: "#fbbf24", border: "#78350f", glow: "#fbbf2444" },
  warning:  { bg: "#3a2e10", text: "#fbbf24", border: "#78350f", glow: "#fbbf2444" },
  low:      { bg: "#1a2e1a", text: "#4ade80", border: "#14532d", glow: "#4ade8044" },
  info:     { bg: "#152638", text: "#60a5fa", border: "#1e3a5f", glow: "#60a5fa44" },
};

function getSeverityStyle(severity?: string) {
  const key = severity?.toLowerCase() ?? "";
  return SEVERITY_COLORS[key] ?? { bg: "#222", text: "#a1a1aa", border: "#444", glow: "#a1a1aa22" };
}

function MetaField({
  label,
  children,
  span,
}: {
  label: string;
  children: React.ReactNode;
  span?: boolean;
}) {
  return (
    <div
      style={{
        gridColumn: span ? "1 / -1" : undefined,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "12px 14px",
        borderRadius: 8,
        background: "#18181b",
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      <span
        style={{
          fontSize: "0.7rem",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "#52525b",
        }}
      >
        {label}
      </span>
      <div
        style={{
          fontSize: "0.875rem",
          color: "#e4e4e7",
          fontWeight: 500,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function AlertDetailView({ alert }: AlertDetailViewProps) {
  const sevStyle = getSeverityStyle(alert.severity ?? undefined);

  return (
    <div
      style={{
        maxWidth: 720,
        margin: "0 auto",
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      <div
        style={{
          padding: "20px",
          borderRadius: 12,
          background: "#111113",
          border: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: sevStyle.text,
              boxShadow: `0 0 8px ${sevStyle.text}`,
              flexShrink: 0,
            }}
            aria-hidden="true"
          />
          <h2
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "#71717a",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Alert
          </h2>
          {alert.severity && (
            <span
              style={{
                marginLeft: "auto",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                padding: "2px 9px",
                borderRadius: 5,
                background: sevStyle.bg,
                color: sevStyle.text,
                border: `1px solid ${sevStyle.border}`,
                boxShadow: `0 0 8px ${sevStyle.glow}`,
              }}
            >
              {alert.severity}
            </span>
          )}
        </div>
        <p
          style={{
            margin: 0,
            fontSize: "1rem",
            fontWeight: 500,
            color: "#e4e4e7",
            lineHeight: 1.5,
          }}
        >
          {alert.message ?? "—"}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 8,
        }}
      >
        <MetaField label="Created">
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {new Date(alert.created_at).toLocaleString()}
          </span>
        </MetaField>

        <MetaField label="Rule ID">
          <span
            style={{
              fontFamily: "monospace",
              fontSize: "0.78rem",
              wordBreak: "break-all",
              color: "#a1a1aa",
            }}
          >
            {alert.rule_id ?? "—"}
          </span>
        </MetaField>

        <MetaField label="Device" span>
          {alert.device_id ? (
            <Link
              to={`/devices/${alert.device_id}`}
              style={{
                fontFamily: "monospace",
                fontSize: "0.78rem",
                color: "#60a5fa",
                textDecoration: "none",
                wordBreak: "break-all",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = "none";
              }}
            >
              {alert.device_id}
            </Link>
          ) : (
            <span style={{ color: "#52525b" }}>—</span>
          )}
        </MetaField>

        <MetaField label="Workspace">
          <span
            style={{
              fontFamily: "monospace",
              fontSize: "0.78rem",
              wordBreak: "break-all",
              color: "#a1a1aa",
            }}
          >
            {alert.tenant_id ?? "—"}
          </span>
        </MetaField>
      </div>

      <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <h3
          style={{
            margin: 0,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: "#71717a",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          Trigger conditions
        </h3>

        {alert.rule ? (
          <div
            style={{
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.08)",
              background: "#111113",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 14px",
                borderBottom: "1px solid rgba(255,255,255,0.07)",
                gap: 8,
              }}
            >
              <span
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "#e4e4e7",
                }}
              >
                {alert.rule.name ?? "—"}
              </span>
              {!alert.rule.is_active && (
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    padding: "2px 7px",
                    borderRadius: 4,
                    background: "#2a1f0a",
                    color: "#fbbf24",
                    border: "1px solid #78350f",
                  }}
                >
                  Inactive
                </span>
              )}
            </div>

            <pre
              style={{
                margin: 0,
                maxHeight: 420,
                overflowY: "auto",
                padding: "14px",
                fontSize: "0.75rem",
                lineHeight: 1.7,
                color: "#a1a1aa",
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                background: "transparent",
                scrollbarWidth: "thin",
                scrollbarColor: "#3f3f46 transparent",
              }}
            >
              {JSON.stringify(alert.rule.conditions ?? {}, null, 2)}
            </pre>
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "14px",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.07)",
              background: "#111113",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#52525b"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p style={{ margin: 0, fontSize: "0.875rem", color: "#52525b" }}>
              No rule linked to this alert (or rule was deleted).
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
